import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import type {
    GenerateTitleDetailResponse,
    GenerationListResponse,
    GenerationStatus,
} from '@/common/api/client/types.gen'
import { formatDate } from '@/common/lib/dateUtils'
import { cn } from '@/common/lib/cn/cn'
import { CollapsibleCard, CollapsibleCardGroup, useCollapsibleCardGroup } from '@/common/ui/CollapsibleCard'
import { ExpandableText } from '@/common/ui/ExpandableText'
import { Button } from '@/common/ui/Form/Button'
import { Icon } from '@/common/ui/Icon'
import { Pagination } from '@/common/ui/Pagination'
import { Spinner } from '@/common/ui/Spinner'
import { listQueryKey, useProductFlowListRowDetailQuery } from '../../model/productFlow'
import { fetchTextsGenerationList } from '../../model/textsGeneration'
import type { ProductTextsGenerationState } from '../../model/textsGeneration'
import { ProductImportCollapsible, type MarketplaceTextsSelection } from './ProductImportCollapsible'

const isGenerationRunning = (status: GenerationStatus | undefined): boolean => {
    return status === 'running' || status === 'pending' || status === 'created'
}

type TextsGenerationCollapsibleProps = {
    item: GenerationListResponse
    state: ProductTextsGenerationState
    disableAnimation?: boolean
    selectionMode?: boolean
    onSelect?: (data: MarketplaceTextsSelection) => void
}

const TextsGenerationCollapsible = ({
    item,
    state,
    disableAnimation = false,
    selectionMode = false,
    onSelect,
}: TextsGenerationCollapsibleProps) => {
    const collapsibleGroup = useCollapsibleCardGroup()
    const { flowKey, orgId, fetchDetail, pollIntervalMs, terminalStatuses, generationId, detailFormPipelineLoading } =
        state
    const isOpen = collapsibleGroup?.openItemId === item.id
    const isActiveGeneration = item.id === generationId
    const isActivePipeline = isActiveGeneration && detailFormPipelineLoading

    const isListItemRunning = isGenerationRunning(item.status)
    const [fetchForSelect, setFetchForSelect] = useState(false)
    const shouldPollDetail = isOpen || isListItemRunning || fetchForSelect

    const rowDetailQuery = useProductFlowListRowDetailQuery<GenerateTitleDetailResponse>({
        flowKey,
        orgId,
        resourceId: item.id,
        fetchDetail,
        terminalStatuses,
        pollIntervalMs,
        enabled: shouldPollDetail,
    })

    const generationStatus = rowDetailQuery.data?.status ?? item.status
    const isRunning = isGenerationRunning(generationStatus)
    const isFailed = generationStatus === 'failed'
    const displayDate = rowDetailQuery.data?.completed_at ?? rowDetailQuery.data?.created_at ?? item.created_at
    const generatedTitle = rowDetailQuery.data?.validation_result?.title
    const generatedDescription = rowDetailQuery.data?.validation_result?.description
    const hasGeneratedContent = Boolean(generatedTitle?.trim() || generatedDescription?.trim())
    const fallbackTitle = item.name?.trim() || `Генерация ${item.id.slice(0, 8)}…`

    const isSelectButtonLoading =
        selectionMode &&
        fetchForSelect &&
        !hasGeneratedContent &&
        !isFailed &&
        (rowDetailQuery.isLoading || rowDetailQuery.isFetching || isRunning)

    const showInitialLoading = isOpen && shouldPollDetail && rowDetailQuery.isLoading && !rowDetailQuery.data
    const showRunningLoader = isOpen && isRunning && !hasGeneratedContent
    const showFailedState = isOpen && isFailed && !hasGeneratedContent

    useEffect(() => {
        setFetchForSelect(false)
    }, [item.id])

    useEffect(() => {
        if (!fetchForSelect || !hasGeneratedContent || !onSelect) {
            return
        }

        onSelect({
            title: generatedTitle?.trim() || '',
            description: generatedDescription?.trim() || '',
        })
        setFetchForSelect(false)
    }, [fetchForSelect, generatedDescription, generatedTitle, hasGeneratedContent, onSelect])

    const handleSelect = () => {
        if (!hasGeneratedContent || !onSelect) {
            return
        }

        onSelect({
            title: generatedTitle?.trim() || '',
            description: generatedDescription?.trim() || '',
        })
    }

    const handleSelectClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()

        if (hasGeneratedContent) {
            handleSelect()
            return
        }

        if (isFailed || isSelectButtonLoading) {
            return
        }

        setFetchForSelect(true)
    }

    const handleSelectKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        event.stopPropagation()
    }

    return (
        <CollapsibleCard
            type={isActiveGeneration ? 'result' : undefined}
            title={fallbackTitle}
            groupItemId={item.id}
            ariaLabel={`Генерация текста ${item.name || item.id}`}
            description={displayDate ? formatDate(displayDate) : undefined}
            className={cn('w-full', {
                'border-[var(--pixorion-light-violet)]': isActivePipeline,
            })}
            style={isActivePipeline ? { borderColor: 'var(--pixorion-light-violet)' } : undefined}
            disableAnimation={disableAnimation}
            headerAction={
                selectionMode ? (
                    <Button
                        type='button'
                        size='md'
                        variant='outline'
                        loading={isSelectButtonLoading}
                        onClick={handleSelectClick}
                        onKeyDown={handleSelectKeyDown}
                        aria-label={`Выбрать генерацию ${fallbackTitle}`}
                    >
                        Выбрать
                    </Button>
                ) : undefined
            }
        >
            {showInitialLoading && (
                <div className='flex items-center justify-center py-8 w-full' role='status' aria-live='polite'>
                    <Spinner className='w-12 h-12' />
                </div>
            )}
            {rowDetailQuery.isError && (
                <div className='text-sm text-red-400' role='alert'>
                    Ошибка загрузки генерации {item.id}
                </div>
            )}
            {!showInitialLoading && !rowDetailQuery.isError && showRunningLoader && (
                <div
                    className='flex items-center justify-center py-8 w-full min-h-[120px]'
                    role='status'
                    aria-live='polite'
                >
                    <Spinner className='w-12 h-12' />
                </div>
            )}
            {!showInitialLoading && !rowDetailQuery.isError && showFailedState && (
                <div
                    className='flex flex-col items-center justify-center gap-3 py-8 w-full min-h-[120px]'
                    role='status'
                >
                    <Icon name='cross' className='w-12 h-12' />
                    <p className='text-sm text-[#aba7af]'>Генерация не удалась</p>
                </div>
            )}
            {!showInitialLoading && !rowDetailQuery.isError && !showRunningLoader && !showFailedState && (
                <div className='flex flex-col gap-4 w-full'>
                    <div className='flex flex-col gap-2 w-full'>
                        <h4 className='font-medium leading-[22px] text-base text-white'>Название</h4>
                        <p className='text-sm text-[var(--pixorion-gray-2)]'>{generatedTitle || '-'}</p>
                    </div>
                    <div className='flex flex-col gap-2 w-full'>
                        <h4 className='font-medium leading-[22px] text-base text-white'>Описание</h4>
                        <ExpandableText
                            text={generatedDescription || ''}
                            markdown
                            wordLimit={30}
                            showCharacterCount
                            defaultExpanded={isActiveGeneration}
                        />
                    </div>
                </div>
            )}
        </CollapsibleCard>
    )
}

type TextsGenerationsListItemsProps = {
    listItems: GenerationListResponse[]
    state: ProductTextsGenerationState
    shouldTrackNewGeneration: boolean
    onNewGenerationHandled: () => void
    selectionMode?: boolean
    onSelect?: (data: MarketplaceTextsSelection) => void
}

const TextsGenerationsListItems = ({
    listItems,
    state,
    shouldTrackNewGeneration,
    onNewGenerationHandled,
    selectionMode = false,
    onSelect,
}: TextsGenerationsListItemsProps) => {
    const collapsibleGroup = useCollapsibleCardGroup()
    const prevItemIdsRef = useRef<string[]>([])
    const [disableAnimationItemId, setDisableAnimationItemId] = useState<string | null>(null)

    useLayoutEffect(() => {
        const itemIds = listItems.map(item => item.id).filter(Boolean)
        const openItemExists = itemIds.includes(collapsibleGroup?.openItemId ?? '')
        const hasInteracted = collapsibleGroup?.hasInteracted ?? false

        if (!itemIds.length) {
            prevItemIdsRef.current = itemIds
            return
        }

        if (shouldTrackNewGeneration) {
            const prevItemIds = prevItemIdsRef.current
            const newItemId = itemIds.find(id => !prevItemIds.includes(id))

            if (newItemId) {
                collapsibleGroup?.setHasInteracted(false)
                collapsibleGroup?.setOpenItemId(newItemId)
                requestAnimationFrame(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                })
                onNewGenerationHandled()
                prevItemIdsRef.current = itemIds
                return
            }
        }

        if (!openItemExists && !hasInteracted) {
            const firstItemId = itemIds[0] ?? null
            setDisableAnimationItemId(firstItemId)
            collapsibleGroup?.setOpenItemId(firstItemId)
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setDisableAnimationItemId(null)
                })
            })
        }

        prevItemIdsRef.current = itemIds
    }, [collapsibleGroup, listItems, onNewGenerationHandled, shouldTrackNewGeneration])

    return (
        <ul className='flex flex-col gap-[12px] w-full list-none p-0 m-0' role='list'>
            {listItems.map(item => {
                if (!item.id) {
                    return null
                }

                return (
                    <li key={item.id} role='listitem'>
                        <TextsGenerationCollapsible
                            item={item}
                            state={state}
                            disableAnimation={disableAnimationItemId === item.id}
                            selectionMode={selectionMode}
                            onSelect={onSelect}
                        />
                    </li>
                )
            })}
        </ul>
    )
}

type PageListLike = {
    items?: Array<{ id?: string }>
    pages?: number | null
}

type TextsGenerationsListProps = {
    state: ProductTextsGenerationState
    emptyListMessage?: string
    selectionMode?: boolean
    onSelect?: (data: MarketplaceTextsSelection) => void
}

export const TextsGenerationsList = ({
    state,
    emptyListMessage = 'Нет генераций для этого товара. Запустите генерацию.',
    selectionMode = false,
    onSelect,
}: TextsGenerationsListProps) => {
    const {
        listQuery,
        orgId,
        productId,
        product,
        flowKey,
        listCurrentPage,
        listPageSize,
        handleListPageChange,
        generationMutation,
    } = state

    const [localListPage, setLocalListPage] = useState(1)
    const [shouldTrackNewGeneration, setShouldTrackNewGeneration] = useState(false)

    useEffect(() => {
        setLocalListPage(1)
    }, [orgId, productId])

    const localListQuery = useQuery({
        queryKey: listQueryKey(flowKey, orgId, productId, localListPage, listPageSize),
        queryFn: () =>
            fetchTextsGenerationList({
                orgId: orgId!,
                productId: productId!,
                page: localListPage,
                pageSize: listPageSize,
            }),
        enabled: Boolean(selectionMode && orgId && productId),
    })

    const activeListQuery = selectionMode ? localListQuery : listQuery
    const activeCurrentPage = selectionMode ? localListPage : listCurrentPage
    const activeHandlePageChange = selectionMode ? setLocalListPage : handleListPageChange

    useEffect(() => {
        if (selectionMode) {
            return
        }

        if (generationMutation.isError) {
            setShouldTrackNewGeneration(false)
            return
        }

        if (generationMutation.isPending) {
            setShouldTrackNewGeneration(true)
        }
    }, [generationMutation.isError, generationMutation.isPending, selectionMode])

    const listPayload = activeListQuery.data ?? null
    const listItems = (listPayload as PageListLike | null)?.items ?? []
    const totalPages = Math.max(1, (listPayload as PageListLike | null)?.pages ?? 1)
    const importUpdateDate = product?.last_updated

    if (!orgId || !productId) {
        return null
    }

    if (activeListQuery.isLoading) {
        return (
            <div className='text-sm text-[#aba7af]' role='status' aria-live='polite'>
                Загрузка списка…
            </div>
        )
    }

    if (activeListQuery.isError) {
        return (
            <div className='text-sm text-red-400' role='alert'>
                Ошибка загрузки списка
            </div>
        )
    }

    return (
        <section
            aria-label={selectionMode ? 'Выбор Текстов и SEO' : 'Список генераций текстов по товару'}
            className='flex flex-col gap-3 w-full'
        >
            {listItems.length === 0 && (
                <div className='text-sm text-[#aba7af] hidden md:block' role='status'>
                    {emptyListMessage}
                </div>
            )}
            {listItems.length > 0 && (
                <CollapsibleCardGroup>
                    <TextsGenerationsListItems
                        listItems={listItems as GenerationListResponse[]}
                        state={state}
                        shouldTrackNewGeneration={selectionMode ? false : shouldTrackNewGeneration}
                        onNewGenerationHandled={() => setShouldTrackNewGeneration(false)}
                        selectionMode={selectionMode}
                        onSelect={onSelect}
                    />
                </CollapsibleCardGroup>
            )}
            {product && (
                <ProductImportCollapsible
                    productName={product.name}
                    productDescription={product.description || ''}
                    importUpdateDate={importUpdateDate}
                    selectionMode={selectionMode}
                    onSelect={onSelect}
                />
            )}
            <Pagination
                className='mt-1 w-full justify-center'
                currentPage={activeCurrentPage}
                totalPages={totalPages}
                onPageChange={activeHandlePageChange}
            />
        </section>
    )
}

export type { MarketplaceTextsSelection }
